// Copyright (c) Zammo Inc.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import './App.css';
import { createTheme, initializeIcons, loadTheme } from '@fluentui/react';

import Header from './components/Header';
import TokenModal from './components/TokenModal/indext';
import { canUrlCanBeFetchedInIframe, getSimulatorSession, getUrl, SimulatorSession } from './app/actions';
import Simulator from './components/Simulator';
import { htmlLinksAbsolutify, isIOS } from './utils/helpers';
import VoiceOnlySimulator from './components/VoiceOnlySimulator/VoiceOnlySimulator';
import VoiceWelcomeModal from './components/VoiceOnlySimulator/VoiceWelcomeModal';
import SessionFailedModal from './components/Modals/SessionFailedModal';

const FakeIframe = styled.div`
  width: 100%;
  border: none;
  height: calc(100vh - 64px);
`;

const Iframe = styled.iframe`
  width: 100%;
  border: none;
  height: calc(100vh - 64px);
`;

initializeIcons('https://res.cdn.office.net/files/fabric-cdn-prod_20241209.001/assets/icons/');

loadTheme(
  createTheme({
    defaultFontStyle: { fontFamily: 'Montserrat', fontWeight: 'normal' },
  })
);

const App = () => {
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showFakeIframe, setShowFakeIframe] = useState(false);
  const [showVoiceSimulator, setShowVoiceSimulator] = useState(false);
  const [isSessionFailed, setIsSessionFailed] = useState(false);
  const [simulatorSession, setSimulatorSession] = useState<SimulatorSession>();
  const [isLoading, setIsLoading] = useState(false);
  const [simulatorToken, setSimulatorToken] = useState('');
  const [url, setUrl] = useState('');
  const [fakeIframeContent, setFakeIframeContent] = useState('');
  const location = useLocation();
  const [isVoice, setIsVoice] = useState(true);

  useEffect(() => {
    const queries = queryString.parse(location.search);
    if (queries.token) {
      setSimulatorToken(`${queries.token}`);
    } else {
      setShowTokenModal(true);
    }
    if (queries.channel) {
      setIsVoice(queries.channel === 'voice');
    } else {
      setIsVoice(false); // default to chat
    }

    if (queries.url) {
      setUrl(`${queries.url}`);
    }
  }, [location]);

  useEffect(() => {
    if (simulatorToken) {
      getSimulatorSession(simulatorToken)
        .then((data: SimulatorSession) => {
          setSimulatorSession(data);
        })
        .catch(() => {
          setIsSessionFailed(true);
        });
    }
  }, [simulatorToken]);

  useEffect(() => {
    try {
      if (url) {
        setIsLoading(true);
        canUrlCanBeFetchedInIframe(url).then((res) => {
          if (!res) {
            getUrl(url).then((data) => {
              const cleanedHtmlContent = data
                .replace(new RegExp('\\\\"', 'g'), '"')
                .replace(/\\n/g, '')
                .replace(/\\t/g, '')
                .replace(/\\\\x22/g, '"');

              const htmlWithUrlFixed = htmlLinksAbsolutify(cleanedHtmlContent, url);
              setShowFakeIframe(true);
              setFakeIframeContent(htmlWithUrlFixed);
              setIsLoading(false);
            });
          } else {
            setIsLoading(false);
          }
        });
      }
    } catch (err) {
      setIsLoading(false);
      setShowFakeIframe(false);
      setFakeIframeContent('');
    }
  }, [url]);

  return (
    <>
      <Header setURL={setUrl} isShowURLInput={!isVoice && !url} />
      {!isLoading && url && !showFakeIframe && <Iframe src={url} />}
      {!isLoading && url && showFakeIframe && (
        <FakeIframe
          dangerouslySetInnerHTML={{
            __html: fakeIframeContent,
          }}
        />
      )}

      {simulatorToken && simulatorSession && !isVoice && (
        <Simulator simulatorToken={simulatorToken} session={simulatorSession} />
      )}

      {simulatorToken && simulatorSession && isVoice && (showVoiceSimulator || isIOS()) && (
        <VoiceOnlySimulator
          simulatorToken={simulatorToken}
          directLineToken={simulatorSession.directLineToken}
          speechToken={simulatorSession.speechToken}
          speechRegion={simulatorSession.speechRegion}
          speechVoiceNames={simulatorSession.speechVoiceNames}
        />
      )}

      {!simulatorToken && <TokenModal isOpen={showTokenModal} onDismiss={() => setShowTokenModal(false)} />}

      <VoiceWelcomeModal
        isOpen={!!simulatorSession && isVoice && !showVoiceSimulator}
        onConfirm={() => setShowVoiceSimulator(true)}
      />

      <SessionFailedModal isOpen={isSessionFailed} />
    </>
  );
};

export default App;
